import React from 'react'
import { useAuth } from '../context/AuthContext'
import { contractService } from '../services/contractService'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog'
import {
  FileText, Filter, Plus, Upload, Download, Trash2, ChevronDown, ChevronUp, Pencil, AlertTriangle,
} from 'lucide-react'
import {
  Contract, ContractFile, CONTRACT_TYPES, CONTRACT_STATUSES,
  CreateContractRequest, UpdateContractRequest,
} from '../types/contracts'

// ─── Local helpers ────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

function formatDate(value?: string | null): string {
  if (!value) return '—'
  // Server sends DateOnly as 'YYYY-MM-DD' — parse without timezone shift.
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return value
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function formatCurrency(value?: number | null, currency?: string | null): string {
  if (value == null) return '—'
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD' }).format(value)
  } catch {
    return `${currency || ''} ${value.toFixed(2)}`.trim()
  }
}

function daysUntil(value?: string | null): number | null {
  if (!value) return null
  const [y, m, d] = value.split('-').map(Number)
  if (!y || !m || !d) return null
  const target = new Date(y, m - 1, d).getTime()
  const today = new Date(); today.setHours(0, 0, 0, 0)
  return Math.round((target - today.getTime()) / 86_400_000)
}

function renewalChip(contract: Contract): React.ReactElement | null {
  const days = daysUntil(contract.renewalDate ?? contract.endDate)
  if (days == null) return null
  if (days < 0) return <Badge className="bg-rose-100 text-rose-800">Overdue</Badge>
  if (days < 30) return <Badge className="bg-rose-100 text-rose-800">Renews in {days}d</Badge>
  if (days < 90) return <Badge className="bg-amber-100 text-amber-800">Renews in {days}d</Badge>
  return <Badge className="bg-emerald-100 text-emerald-800">Renews in {days}d</Badge>
}

function statusBadge(status: string): React.ReactElement {
  switch (status) {
    case 'Active':    return <Badge variant="success">Active</Badge>
    case 'Expired':   return <Badge variant="destructive">Expired</Badge>
    case 'Pending':   return <Badge className="bg-slate-100 text-slate-700">Pending</Badge>
    case 'Cancelled': return <Badge className="bg-slate-100 text-slate-500">Cancelled</Badge>
    default:          return <Badge variant="default">{status}</Badge>
  }
}

// ─── Form modal ───────────────────────────────────────────────────

interface ContractFormState {
  customerId: string
  contractType: string
  title: string
  vendor: string
  annualValue: string
  currency: string
  startDate: string
  endDate: string
  renewalDate: string
  autoRenew: boolean
  status: string
  notes: string
}

const EMPTY_FORM: ContractFormState = {
  customerId: '',
  contractType: 'MSA',
  title: '',
  vendor: '',
  annualValue: '',
  currency: 'AUD',
  startDate: '',
  endDate: '',
  renewalDate: '',
  autoRenew: false,
  status: 'Active',
  notes: '',
}

function contractToForm(c: Contract): ContractFormState {
  return {
    customerId: c.customerId,
    contractType: c.contractType,
    title: c.title,
    vendor: c.vendor || '',
    annualValue: c.annualValue != null ? String(c.annualValue) : '',
    currency: c.currency || 'AUD',
    startDate: c.startDate || '',
    endDate: c.endDate || '',
    renewalDate: c.renewalDate || '',
    autoRenew: c.autoRenew,
    status: c.status,
    notes: c.notes || '',
  }
}

// ─── Page ─────────────────────────────────────────────────────────

export default function ContractsView() {
  const { partner, managedCustomers } = useAuth()
  const partnerId = partner?.partnerId || ''

  const [contracts, setContracts] = React.useState<Contract[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)

  // Filters
  const [customerFilter, setCustomerFilter] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('')

  // Expanded rows + per-row file detail
  const [expandedId, setExpandedId] = React.useState<string | null>(null)
  const [filesByContract, setFilesByContract] = React.useState<Record<string, ContractFile[]>>({})
  const [filesLoading, setFilesLoading] = React.useState<Record<string, boolean>>({})

  // Create / edit dialog
  const [formOpen, setFormOpen] = React.useState(false)
  const [editingContract, setEditingContract] = React.useState<Contract | null>(null)
  const [form, setForm] = React.useState<ContractFormState>(EMPTY_FORM)
  const [isSaving, setIsSaving] = React.useState(false)

  // Delete confirm
  const [deletingId, setDeletingId] = React.useState<string | null>(null)
  const [isDeleting, setIsDeleting] = React.useState(false)

  // Per-row upload state
  const [uploadingId, setUploadingId] = React.useState<string | null>(null)

  const flashSuccess = (msg: string) => {
    setSuccessMessage(msg)
    setTimeout(() => setSuccessMessage(null), 3000)
  }

  const loadContracts = React.useCallback(async () => {
    if (!partnerId) return
    setIsLoading(true)
    setError(null)
    try {
      const params: { customerId?: string; status?: string } = {}
      if (customerFilter) params.customerId = customerFilter
      if (statusFilter) params.status = statusFilter
      const res = await contractService.list(partnerId, params)
      if (res.success && res.data) setContracts(res.data)
      else setError(res.error || 'Failed to load contracts')
    } catch {
      setError('Failed to load contracts')
    } finally {
      setIsLoading(false)
    }
  }, [partnerId, customerFilter, statusFilter])

  React.useEffect(() => { loadContracts() }, [loadContracts])

  const customerName = React.useCallback((customerId: string) => {
    return managedCustomers.find(c => c.customerId === customerId)?.customerName || customerId
  }, [managedCustomers])

  const expandRow = async (contractId: string) => {
    if (expandedId === contractId) {
      setExpandedId(null)
      return
    }
    setExpandedId(contractId)
    if (!filesByContract[contractId]) {
      setFilesLoading(prev => ({ ...prev, [contractId]: true }))
      try {
        const res = await contractService.get(partnerId, contractId)
        if (res.success && res.data) {
          setFilesByContract(prev => ({ ...prev, [contractId]: res.data?.files ?? [] }))
        }
      } finally {
        setFilesLoading(prev => ({ ...prev, [contractId]: false }))
      }
    }
  }

  const refreshFiles = async (contractId: string) => {
    const res = await contractService.get(partnerId, contractId)
    if (res.success && res.data) {
      setFilesByContract(prev => ({ ...prev, [contractId]: res.data?.files ?? [] }))
    }
  }

  // ─── Create / edit ──────────────────────────────────────────────

  const openCreate = () => {
    setEditingContract(null)
    setForm({ ...EMPTY_FORM, customerId: customerFilter || managedCustomers[0]?.customerId || '' })
    setFormOpen(true)
  }

  const openEdit = (contract: Contract) => {
    setEditingContract(contract)
    setForm(contractToForm(contract))
    setFormOpen(true)
  }

  const handleSave = async () => {
    setError(null)
    if (!form.title.trim()) { setError('Title is required'); return }
    if (!editingContract && !form.customerId) { setError('Customer is required'); return }

    const annualValue = form.annualValue.trim() ? Number(form.annualValue) : null
    if (annualValue != null && !Number.isFinite(annualValue)) {
      setError('Annual value must be a number'); return
    }

    setIsSaving(true)
    try {
      if (editingContract) {
        const payload: UpdateContractRequest = {
          title: form.title,
          contractType: form.contractType,
          vendor: form.vendor || null,
          annualValue,
          currency: form.currency || null,
          startDate: form.startDate || null,
          endDate: form.endDate || null,
          renewalDate: form.renewalDate || null,
          autoRenew: form.autoRenew,
          status: form.status,
          notes: form.notes || null,
        }
        const res = await contractService.update(partnerId, editingContract.id, payload)
        if (res.success) { flashSuccess('Contract updated'); setFormOpen(false); await loadContracts() }
        else setError(res.error || 'Failed to update contract')
      } else {
        const payload: CreateContractRequest = {
          title: form.title,
          contractType: form.contractType,
          vendor: form.vendor || undefined,
          annualValue: annualValue ?? undefined,
          currency: form.currency || undefined,
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
          renewalDate: form.renewalDate || undefined,
          autoRenew: form.autoRenew,
          status: form.status,
          notes: form.notes || undefined,
        }
        const res = await contractService.create(partnerId, form.customerId, payload)
        if (res.success) { flashSuccess('Contract created'); setFormOpen(false); await loadContracts() }
        else setError(res.error || 'Failed to create contract')
      }
    } catch {
      setError('Save failed')
    } finally {
      setIsSaving(false)
    }
  }

  // ─── Delete ─────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deletingId) return
    setIsDeleting(true)
    setError(null)
    try {
      const res = await contractService.remove(partnerId, deletingId)
      if (res.success) {
        flashSuccess('Contract deleted')
        setDeletingId(null)
        if (expandedId === deletingId) setExpandedId(null)
        await loadContracts()
      } else {
        setError(res.error || 'Failed to delete contract')
      }
    } catch {
      setError('Delete failed')
    } finally {
      setIsDeleting(false)
    }
  }

  // ─── Files ──────────────────────────────────────────────────────

  const handleFilePick = async (contractId: string, file: File | null) => {
    if (!file) return
    if (file.type !== 'application/pdf') { setError('Only PDF files are supported'); return }
    if (file.size > 25 * 1024 * 1024) { setError('File exceeds 25MB limit'); return }

    setUploadingId(contractId)
    setError(null)
    try {
      const res = await contractService.uploadFile(partnerId, contractId, file)
      if (res.success) {
        flashSuccess('File uploaded')
        await refreshFiles(contractId)
        await loadContracts()
      } else {
        setError(res.error || 'Failed to upload')
      }
    } catch {
      setError('Upload failed')
    } finally {
      setUploadingId(null)
    }
  }

  const handleFileDownload = async (contractId: string, fileId: string) => {
    try {
      const res = await contractService.getDownloadUrl(partnerId, contractId, fileId)
      if (res.success && res.data) {
        window.open(res.data.url, '_blank')
      } else {
        setError('Failed to get download link')
      }
    } catch {
      setError('Download failed')
    }
  }

  const handleFileDelete = async (contractId: string, fileId: string) => {
    if (!window.confirm('Delete this file?')) return
    try {
      const res = await contractService.deleteFile(partnerId, contractId, fileId)
      if (res.success) {
        flashSuccess('File deleted')
        await refreshFiles(contractId)
        await loadContracts()
      } else {
        setError(res.error || 'Failed to delete file')
      }
    } catch {
      setError('Delete failed')
    }
  }

  // ─── Derived ────────────────────────────────────────────────────

  const renewingSoonCount = contracts.filter(c => {
    const d = daysUntil(c.renewalDate ?? c.endDate)
    return d != null && d <= 90 && c.status === 'Active'
  }).length

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Contracts</h1>
            <p className="text-sm text-slate-500 mt-1">
              Customer contracts, agreements and renewals
              {renewingSoonCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                  {renewingSoonCount} renewing soon
                </span>
              )}
            </p>
          </div>
          <Button onClick={openCreate} className="inline-flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            New contract
          </Button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {successMessage && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 bg-white rounded-lg border border-slate-200 p-4">
          <Filter className="h-4 w-4 text-slate-400" />
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Customer</label>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Customers</option>
              {managedCustomers.map((c) => (
                <option key={c.customerId} value={c.customerId}>{c.customerName}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Statuses</option>
              {CONTRACT_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin" />
          </div>
        ) : contracts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No contracts</h3>
            <p className="text-sm text-slate-500 mt-1">
              {customerFilter || statusFilter
                ? 'No contracts match the current filters'
                : 'Add a contract to track agreements, renewals and signed documents.'}
            </p>
            {!(customerFilter || statusFilter) && (
              <Button onClick={openCreate} className="mt-4 inline-flex items-center">
                <Plus className="h-4 w-4 mr-2" />
                New contract
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {contracts.map((contract) => {
              const isExpanded = expandedId === contract.id
              const files = filesByContract[contract.id] || []
              const filesAreLoading = filesLoading[contract.id]
              return (
                <div key={contract.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => expandRow(contract.id)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{contract.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {customerName(contract.customerId)} · {contract.contractType}
                          {contract.annualValue != null && ` · ${formatCurrency(contract.annualValue, contract.currency)}/yr`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {renewalChip(contract)}
                      {statusBadge(contract.status)}
                      {(contract.fileCount ?? 0) > 0 && (
                        <span className="text-xs text-slate-500">
                          {contract.fileCount} file{contract.fileCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {isExpanded
                        ? <ChevronUp className="h-4 w-4 text-slate-400" />
                        : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-slate-200 px-6 py-4 bg-slate-50 space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Vendor</div>
                          <div className="text-slate-900 mt-0.5">{contract.vendor || '—'}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Start</div>
                          <div className="text-slate-900 mt-0.5">{formatDate(contract.startDate)}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">End</div>
                          <div className="text-slate-900 mt-0.5">{formatDate(contract.endDate)}</div>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Renewal</div>
                          <div className="text-slate-900 mt-0.5">
                            {formatDate(contract.renewalDate)}
                            {contract.autoRenew && <span className="ml-2 text-xs text-slate-500">(auto)</span>}
                          </div>
                        </div>
                      </div>

                      {contract.notes && (
                        <div>
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Notes</div>
                          <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{contract.notes}</div>
                        </div>
                      )}

                      {/* Files */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Files</div>
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="file"
                              accept="application/pdf"
                              className="hidden"
                              disabled={uploadingId === contract.id}
                              onChange={(e) => {
                                handleFilePick(contract.id, e.target.files?.[0] || null)
                                e.target.value = ''
                              }}
                            />
                            <span className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50">
                              <Upload className="h-3.5 w-3.5 mr-1.5" />
                              {uploadingId === contract.id ? 'Uploading…' : 'Upload PDF'}
                            </span>
                          </label>
                        </div>
                        {filesAreLoading ? (
                          <div className="text-xs text-slate-500">Loading…</div>
                        ) : files.length === 0 ? (
                          <div className="text-xs text-slate-500">No files yet.</div>
                        ) : (
                          <div className="space-y-2">
                            {files.map((file) => (
                              <div key={file.id} className="flex items-center justify-between bg-white rounded-md border border-slate-200 px-4 py-2.5">
                                <div className="flex items-center gap-3 min-w-0">
                                  <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-900 truncate">{file.fileName}</div>
                                    <div className="text-xs text-slate-500">{formatFileSize(file.fileSize)} · {formatDate(file.createdAt.slice(0, 10))}</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Button size="sm" variant="outline" onClick={() => handleFileDownload(contract.id, file.id)}>
                                    <Download className="h-3.5 w-3.5 mr-1.5" />
                                    Download
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleFileDelete(contract.id, file.id)}>
                                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-3 pt-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(contract)}>
                          <Pencil className="h-3.5 w-3.5 mr-1.5" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeletingId(contract.id)}>
                          <Trash2 className="h-3.5 w-3.5 mr-1.5 text-rose-600" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingContract ? 'Edit contract' : 'New contract'}</DialogTitle>
            <DialogDescription>
              {editingContract ? 'Update the contract details below.' : 'Record a customer contract or agreement. You can attach the signed PDF after saving.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer</Label>
                <select
                  value={form.customerId}
                  onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                  disabled={!!editingContract}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-slate-50"
                >
                  <option value="">Select…</option>
                  {managedCustomers.map((c) => (
                    <option key={c.customerId} value={c.customerId}>{c.customerName}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Type</Label>
                <select
                  value={form.contractType}
                  onChange={(e) => setForm({ ...form, contractType: e.target.value })}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {CONTRACT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Acme Corp — MSA 2026"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Vendor</Label>
                <Input
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Annual value</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={form.annualValue}
                  onChange={(e) => setForm({ ...form, annualValue: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Currency</Label>
                <Input
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                  maxLength={8}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Start date</Label>
                <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>Renewal date</Label>
                <Input type="date" value={form.renewalDate} onChange={(e) => setForm({ ...form, renewalDate: e.target.value })} className="mt-1" />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.autoRenew}
                  onChange={(e) => setForm({ ...form, autoRenew: e.target.checked })}
                />
                Auto-renews
              </label>
              <div className="flex items-center gap-2">
                <Label htmlFor="contract-status" className="text-sm">Status</Label>
                <select
                  id="contract-status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {CONTRACT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={isSaving}>
              {editingContract ? 'Save changes' : 'Create contract'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deletingId !== null} onOpenChange={() => setDeletingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete contract?</DialogTitle>
            <DialogDescription>
              The contract record and any uploaded files will be removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} loading={isDeleting}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
